import './image-cropper-dialog.scss';
import {useCallback, useState} from 'react';
import {useEffect} from 'react';
import {translations} from '../../i18n/translations';
import {useTranslation} from 'react-i18next';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Slider,
  Typography,
} from '@mui/material';
import {LoadingButton} from '@mui/lab';
import getCroppedImg from './crop-image';
import Cropper from 'react-easy-crop';

function readFile(file: File) {
  return new Promise((resolve) => {
    const reader = new FileReader();
    reader.addEventListener('load', () => resolve(reader.result), false);
    reader.readAsDataURL(file);
  });
}

/**
 * Image cropper component where the image can be cropped, zoomed and rotated.
 */
export const ImageCropperDialog = (props: {
  open: boolean;
  handleClose: (result?: File) => void;
  imageFile?: File;
  aspect?: number;
  size?: string;
}) => {
  // Hold state
  const [previousImageSrc, setPreviousImageSrc] = useState(null);
  const [imageSrc, setImageSrc] = useState(null);
  const [crop, setCrop] = useState({x: 0, y: 0});
  const [rotation, setRotation] = useState(0);
  const [zoom, setZoom] = useState(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState(null);
  const [isSaving, setIsSaving] = useState(false);

  // Translations
  const {t} = useTranslation();

  // Callbacks
  const handleCloseCancel = () => {
    if (isSaving) return;

    props.handleClose(undefined);
  };
  const handleCloseConfirm = useCallback(async () => {
    if (isSaving) return;

    setIsSaving(true);

    try {
      // Get cropped image
      const croppedImage = await getCroppedImg(
        imageSrc,
        croppedAreaPixels as any,
        rotation,
        props.size as any
      );

      // Done state
      props.handleClose(croppedImage);
    } catch (e) {
      console.error(e);
      setIsSaving(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [croppedAreaPixels, imageSrc, isSaving, rotation]);
  const onCropComplete = useCallback((croppedArea: any, croppedAreaPixels: any) => {
    setCroppedAreaPixels(croppedAreaPixels);
  }, []);

  useEffect(() => {
    if (props.open) {
      setIsSaving(false);
    }
  }, [props.open]);

  // On mount
  useEffect(() => {
    const loadImage = async (file: File) => {
      // Browsers apply EXIF orientation when decoding the image, both in the
      // cropper and in getCroppedImg. Rotating it here would apply it twice.
      const imageDataUrl = await readFile(file);

      // Check for change
      if (imageDataUrl !== previousImageSrc) {
        // New Image, Reset Zoom and Rotation
        setCroppedAreaPixels(null);
        setCrop({x: 0, y: 0});
        setZoom(1);
        setRotation(0);
      }

      // Set Image source
      setImageSrc(imageDataUrl as any);

      // Update previous Image Src
      setPreviousImageSrc(imageDataUrl as any);
    };

    // Load Image
    if (props.imageFile) {
      // Do it
      loadImage(props.imageFile);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.imageFile]);

  // Default result
  return (
    <Dialog onClose={isSaving ? undefined : handleCloseCancel} open={props.open}>
      <DialogTitle className="image-cropper-title">
        {t(translations.image_cropper_dialog.title)}
      </DialogTitle>
      <DialogContent className="image-cropper-content-container">
        <Box className="image-cropper-container">
          <Box className="image-cropper">
            {imageSrc ? (
              <Cropper
                image={imageSrc as any}
                crop={crop}
                rotation={rotation}
                zoom={zoom}
                aspect={props.aspect}
                onCropChange={setCrop}
                onRotationChange={setRotation}
                onCropComplete={onCropComplete}
                onZoomChange={setZoom}
              />
            ) : undefined}
          </Box>
        </Box>
        <Box className="image-cropper-controls">
          <Box className="image-cropper-control">
            <Typography gutterBottom>{t(translations.image_cropper_dialog.zoom)}</Typography>
            <Box className="image-cropper-slider">
              <Slider
                value={zoom}
                min={1}
                max={3}
                step={0.1}
                onChange={(e, zoom) => setZoom(zoom as any)}
                valueLabelDisplay="auto"
                valueLabelFormat={(value) => <div>x{value}</div>}
              />
            </Box>
          </Box>
          <Box className="image-cropper-control">
            <Typography gutterBottom>{t(translations.image_cropper_dialog.rotation)}</Typography>
            <Box className="image-cropper-slider">
              <Slider
                value={rotation}
                min={0}
                max={360}
                step={1}
                onChange={(e, rotation) => setRotation(rotation as any)}
                valueLabelDisplay="auto"
                valueLabelFormat={(value) => <div>{value}º</div>}
              />
            </Box>
          </Box>
        </Box>
      </DialogContent>
      <DialogActions
        className="image-cropper-actions"
        sx={{justifyContent: 'center', gap: '10px', padding: '12px 22px'}}
      >
        <Button
          variant="text"
          color="error"
          sx={{width: '100%', maxWidth: '252px'}}
          onClick={handleCloseCancel}
          disabled={isSaving}
        >
          {t(translations.image_cropper_dialog.cancel_button)}
        </Button>
        <LoadingButton
          variant="contained"
          color="primary"
          sx={{width: '100%', maxWidth: '252px'}}
          onClick={handleCloseConfirm}
          loading={isSaving}
          loadingPosition={isSaving ? 'start' : undefined}
          disabled={isSaving}
        >
          {t(translations.image_cropper_dialog.save_button)}
        </LoadingButton>
      </DialogActions>
    </Dialog>
  );
};
